// src/pages/Login.jsx
import { useState, useRef } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase";

export default function Login() {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const emailRef = useRef();
  const passwordRef = useRef();

  const handleLogin = async () => {
    const email = emailRef.current?.value?.trim();
    const password = passwordRef.current?.value;

    if (!email || !password) {
      setError("이메일과 비밀번호를 모두 입력해주세요.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      console.error("Login error:", err.code, err.message);

      const errorMessages = {
        "auth/user-not-found": "등록되지 않은 이메일입니다.",
        "auth/wrong-password": "비밀번호가 올바르지 않습니다.",
        "auth/invalid-email": "이메일 형식이 올바르지 않습니다.",
        "auth/invalid-credential": "이메일 또는 비밀번호가 올바르지 않습니다.",
        "auth/too-many-requests": "로그인 시도가 너무 많습니다. 잠시 후 다시 시도해주세요.",
        "auth/network-request-failed": "네트워크 오류가 발생했습니다. 연결을 확인해주세요.",
        "auth/api-key-not-valid": "Firebase 설정 오류입니다. .env 파일을 확인해주세요.",
      };

      setError(errorMessages[err.code] || `오류: ${err.code ?? "알 수 없음"}`);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleLogin();
  };

  return (
    <div style={styles.root}>
      <div style={styles.gridBg} />

      <div style={styles.card}>
        <div style={styles.logoWrap}>
          <div style={styles.logoMark}>DS</div>
          <div>
            <div style={styles.logoTitle}>DS ENGINEERING</div>
            <div style={styles.logoSub}>고객사 파일 저장소</div>
          </div>
        </div>

        <div style={styles.divider} />

        <div style={styles.fieldGroup}>
          <label style={styles.label}>이메일</label>
          {/* 입력란: 흰색 배경, 검정 글자 */}
          <input
            ref={emailRef}
            style={styles.input}
            type="email"
            onKeyDown={handleKeyDown}
            placeholder="example@dseng.com"
            autoComplete="email"
            autoFocus
          />
        </div>

        <div style={styles.fieldGroup}>
          <label style={styles.label}>비밀번호</label>
          <input
            ref={passwordRef}
            style={styles.input}
            type="password"
            onKeyDown={handleKeyDown}
            placeholder="••••••••"
            autoComplete="current-password"
          />
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {/* 확인 버튼: 파란색 */}
        <button
          style={{ ...styles.btn, opacity: loading ? 0.7 : 1 }}
          onClick={handleLogin}
          disabled={loading}
        >
          {loading ? "확인 중..." : "확인"}
        </button>
      </div>

      <div style={styles.footer}>© 2026 DS Engineering. All rights reserved.</div>
    </div>
  );
}

const styles = {
  root: {
    minHeight: "100vh",
    background: "#ffffff",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'Pretendard', 'Noto Sans KR', sans-serif",
    position: "relative",
    overflow: "hidden",
  },
  gridBg: {
    position: "absolute",
    inset: 0,
    backgroundImage:
      "linear-gradient(rgba(0,0,0,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.03) 1px, transparent 1px)",
    backgroundSize: "40px 40px",
    pointerEvents: "none",
  },
  card: {
    background: "#fffbeb",
    border: "1px solid #fde68a",
    borderRadius: "12px",
    padding: "40px 44px",
    width: "100%",
    maxWidth: "420px",
    position: "relative",
    zIndex: 1,
    boxShadow: "0 20px 60px rgba(0,0,0,0.08)",
  },
  logoWrap: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    marginBottom: "24px",
  },
  logoMark: {
    width: "44px",
    height: "44px",
    background: "linear-gradient(135deg, #1e6fd9, #0d4fa8)",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontWeight: "800",
    fontSize: "16px",
    letterSpacing: "0.05em",
    flexShrink: 0,
  },
  logoTitle: {
    color: "#111827",
    fontSize: "18px",
    fontWeight: "700",
    letterSpacing: "-0.02em",
  },
  logoSub: { color: "#6b7280", fontSize: "12px", marginTop: "2px" },
  divider: { height: "1px", background: "#fde68a", marginBottom: "28px" },
  fieldGroup: { marginBottom: "18px" },
  label: {
    display: "block",
    color: "#6b7280",
    fontSize: "12px",
    fontWeight: "600",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    marginBottom: "8px",
  },
  /* 입력란: 흰색 배경, 검정 글자 */
  input: {
    width: "100%",
    boxSizing: "border-box",
    background: "#ffffff",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    padding: "12px 14px",
    color: "#111827",
    fontSize: "14px",
    outline: "none",
    transition: "border-color 0.2s",
    fontFamily: "inherit",
  },
  error: {
    background: "rgba(239,68,68,0.08)",
    border: "1px solid rgba(239,68,68,0.4)",
    borderRadius: "6px",
    color: "#dc2626",
    fontSize: "13px",
    padding: "10px 14px",
    marginBottom: "16px",
  },
  /* 확인 버튼: 파란색 */
  btn: {
    width: "100%",
    background: "linear-gradient(135deg, #1e6fd9, #0d4fa8)",
    border: "none",
    borderRadius: "8px",
    padding: "13px",
    color: "#fff",
    fontSize: "15px",
    fontWeight: "700",
    cursor: "pointer",
    letterSpacing: "0.04em",
    marginTop: "4px",
    transition: "opacity 0.2s, transform 0.1s",
    fontFamily: "inherit",
  },
  footer: {
    position: "absolute",
    bottom: "24px",
    color: "#9ca3af",
    fontSize: "11px",
  },
};
